import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const CATEGORIES = ['all', 'romantic', 'seasonal', 'elegant', 'celebration', 'behind_scenes'];

const LABELS = {
  id: { all: 'Semua', romantic: 'Romantis', seasonal: 'Musim', elegant: 'Elegan', celebration: 'Perayaan', behind_scenes: 'Behind the Scenes' },
  en: { all: 'All', romantic: 'Romantic', seasonal: 'Seasonal', elegant: 'Elegant', celebration: 'Celebration', behind_scenes: 'Behind the Scenes' }
};

export default function GalleryPage() {
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const id = language === 'id';

  useEffect(() => {
    setLoading(true);
    const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
    axios.get(`${API}/gallery${params}`)
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-24" data-testid="gallery-page">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? 'Galeri' : 'Gallery'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-7xl text-seikat-bone leading-[0.95]">
              {id ? <>Setiap <em>rangkaian</em>, sebuah <em>puisi</em>.</> : <>Every <em>weave</em>, a <em>poem</em>.</>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/65 max-w-xl">
              {id ? 'Arsip momen yang sudah kami antarkan — untuk diam, dikagumi, dan diingat.' : 'An archive of moments we have delivered — to pause, admire, and remember.'}
            </p>
          </motion.div>

          {/* Category filter */}
          <div className="mt-12 flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATEGORIES.map(cat => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`gallery-filter-${cat}`}
                  data-magnet
                  className={`px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase transition-all ${
                    active ? 'bg-seikat-pink text-seikat-ink' : 'border border-white/10 text-seikat-bone-muted hover:text-seikat-bone hover:border-white/25'
                  }`}
                >
                  {LABELS[id ? 'id' : 'en'][cat] || cat}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="mt-10">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="aspect-[4/3] rounded-2xl glass-dark" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="glass-dark rounded-2xl p-16 text-center">
                <p className="font-body text-seikat-bone/60">{id ? 'Belum ada gambar di kategori ini.' : 'No images in this category yet.'}</p>
              </div>
            ) : (
              <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                  {items.map((item, index) => (
                    <motion.button
                      key={item.item_id || index}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.04, duration: 0.5, ease: EASE }}
                      onClick={() => setLightboxIdx(index)}
                      className="group relative overflow-hidden rounded-2xl aspect-[4/3] text-left"
                      data-testid={`gallery-item-${item.item_id || index}`}
                    >
                      <img
                        src={item.image}
                        alt={id ? item.title_id : item.title_en}
                        className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/85 via-seikat-ink/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <p className="eyebrow-immersive mb-1">{LABELS[id ? 'id' : 'en'][item.category] || item.category}</p>
                        <h3 className="font-heading text-2xl text-seikat-bone">
                          {id ? item.title_id : item.title_en}
                        </h3>
                      </div>
                    </motion.button>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx >= 0 && items[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-seikat-ink/95 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightboxIdx(-1)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightboxIdx(-1)}
              className="absolute top-6 right-6 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors z-10"
              data-testid="lightbox-close"
            >
              <X size={18} />
            </button>
            {items.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => prev > 0 ? prev - 1 : items.length - 1); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors z-10"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => (prev + 1) % items.length); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors z-10"
                >
                  <ChevronRight size={18} />
                </button>
              </>
            )}
            <motion.div
              key={lightboxIdx}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-5xl w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={items[lightboxIdx]?.image} alt="" className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <div className="mt-6 max-w-md mx-auto">
                <p className="eyebrow-immersive">{LABELS[id ? 'id' : 'en'][items[lightboxIdx]?.category] || ''}</p>
                <h3 className="mt-2 font-heading text-3xl text-seikat-bone">{id ? items[lightboxIdx]?.title_id : items[lightboxIdx]?.title_en}</h3>
                <p className="mt-2 font-body text-sm text-seikat-bone/65">{id ? items[lightboxIdx]?.caption_id : items[lightboxIdx]?.caption_en}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ImmersiveLayout>
  );
}
